import React, { useState, useEffect } from 'react';
import { Icon } from '../components/Layout';
import PaymentModal from '../components/PaymentModal';
import Footer from '../components/Footer';
import axios from 'axios';

export default function Premium() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ********** BASE URL **********
  const BASE_URL = "https://api.samasong.com/sama-music";

  // ******************************
  // FETCH SUBSCRIPTION PLANS
  // ******************************
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/subscriptions/getAll`);
        
        if (response.data.success) {
          // Extract plans from the nested data structure
          const plansData = response.data.data.data || [];
          
          // Transform the API response to match our component structure
          const transformedPlans = plansData.map(plan => ({
            id: plan._id,
            name: plan.name,
            description: plan.description,
            price: {
              monthly: plan.type === 'monthly' ? plan.price : 0,
              yearly: plan.type === 'yearly' ? plan.price : (plan.type === 'monthly' ? plan.price * 12 * 0.8 : 0)
            },
            type: plan.type,
            durationInDays: plan.durationInDays,
            features: plan.benefits || [],
            limitations: plan.limitations || [],
            isActive: plan.isActive,
            popular: plan.name.toLowerCase().includes('premium') || plan.name.toLowerCase().includes('pro')
          }));
          
          setPlans(transformedPlans);
        } else {
          setError(response.data.message || 'Failed to load subscription plans');
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(err.response?.data?.message || 'Failed to load subscription plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handlePurchase = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan || plan.price.monthly === 0) return;

    const paymentPlanDetails = {
      subscriptionId: plan.id,
      planId: plan.id,
      planName: plan.name,
      amount: billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly,
      billingCycle: billingCycle,
      currency: 'INR',
      type: plan.type,
      durationInDays: plan.durationInDays,
      isAutoRenew: false,
      originalPlan: plan
    };

    setSelectedPlanForPayment(paymentPlanDetails);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = (result) => {
    if (result.success) {
      // Payment successful
      setTimeout(() => {
        setShowPaymentModal(false);
        alert(`🎉 Payment successful! Your ${result.planDetails.planName} plan has been activated.`);
      }, 2000);
    } else {
      // Payment failed
      console.error('Payment failed:', result.error);
    }
  };

  // Calculate yearly savings for monthly plans
  const calculateYearlySavings = (monthlyPrice) => {
    const yearlyPrice = monthlyPrice * 12;
    const discountedYearly = yearlyPrice * 0.8; // 20% discount
    return yearlyPrice - discountedYearly;
  };

  // Format price display
  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '0.00';
  };

  // Filter plans based on billing cycle
  const getFilteredPlans = () => {
    return plans.filter(plan => {
      if (billingCycle === 'monthly') {
        return plan.type === 'monthly' || plan.price.monthly > 0;
      } else {
        return plan.type === 'yearly' || plan.price.yearly > 0;
      }
    });
  };

  if (loading) {
    return (
      <>
    
          <div className="min-h-screen container mx-auto bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
            <div className="max-w-6xl mx-auto px-4 py-8">
              <div className="flex justify-center items-center min-h-[60vh]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fuchsia-500 mx-auto"></div>
                  <p className="mt-4 text-slate-400">Loading subscription plans...</p>
                </div>
              </div>
            </div>
          </div>
   
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      
            <div className="max-w-6xl mx-auto px-4 py-8">
              <div className="flex justify-center items-center min-h-[60vh]">
                <div className="text-center">
                  <div className="text-red-500 text-4xl mb-4">⚠️</div>
                  <h3 className="text-xl font-bold text-white mb-2">Unable to Load Plans</h3>
                  <p className="text-slate-400 mb-4">{error}</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-6 py-2 rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
  
        <Footer />
      </>
    );
  }

  const filteredPlans = getFilteredPlans();

  return (
    <>
  
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className=" px-4 py-8">
            {/* Header */}
            <div className="text-center space-y-3 xs:space-y-4 mb-8">
              <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold bg-gradient-to-r from-fuchsia-500 to-purple-600 bg-clip-text text-transparent">
                Choose Your Plan
              </h1>
              <p className="text-base xs:text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
                Unlock the full potential of your music experience with our premium plans
              </p>
            </div>

            {/* Billing Toggle - Only show if there are multiple plan types */}
            {plans.some(plan => plan.type === 'monthly') && plans.some(plan => plan.type === 'yearly') && (
              <div className="flex justify-center mb-8">
                <div className="bg-white/5 rounded-lg p-1 border border-white/10">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-3 xs:px-4 sm:px-6 py-1.5 xs:py-2 rounded-md transition text-sm xs:text-base ${
                      billingCycle === 'monthly'
                        ? 'bg-fuchsia-600 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-3 xs:px-4 sm:px-6 py-1.5 xs:py-2 rounded-md transition text-sm xs:text-base ${
                      billingCycle === 'yearly'
                        ? 'bg-fuchsia-600 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span className="hidden xs:inline">Yearly</span>
                    <span className="xs:hidden">Annual</span>
                    {billingCycle === 'yearly' && (
                      <span className="ml-1 xs:ml-2 text-xs bg-green-500 text-white px-1.5 xs:px-2 py-0.5 xs:py-1 rounded">
                        Save 20%
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Plans Grid */}
            {filteredPlans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6 sm:gap-8 mb-12">
                {filteredPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative bg-white/5 backdrop-blur-lg rounded-xl xs:rounded-2xl border-2 transition-all cursor-pointer ${
                      selectedPlan === plan.id
                        ? 'border-fuchsia-500 bg-fuchsia-500/10'
                        : 'border-white/10 hover:border-white/20'
                    } ${plan.popular ? 'ring-2 ring-fuchsia-500/50' : ''}`}
                    onClick={() => handlePlanSelect(plan.id)}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="p-4 xs:p-6 sm:p-8 space-y-4 xs:space-y-6">
                      {/* Plan Header */}
                      <div className="text-center space-y-1 xs:space-y-2">
                        <h3 className="text-lg xs:text-xl sm:text-2xl font-bold text-white capitalize">
                          {plan.name}
                        </h3>
                        <div className="space-y-1">
                          <div className="text-2xl xs:text-3xl sm:text-4xl font-bold text-white">
                            ₹{formatPrice(billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly)}
                            <span className="text-sm xs:text-base sm:text-lg text-slate-400">
                              /{billingCycle === 'monthly' ? 'month' : 'year'}
                            </span>
                          </div>
                          {billingCycle === 'yearly' && plan.type === 'monthly' && plan.price.monthly > 0 && (
                            <div className="text-sm text-green-400">
                              Save ₹{formatPrice(calculateYearlySavings(plan.price.monthly))} annually
                            </div>
                          )}
                          {plan.durationInDays && (
                            <div className="text-xs text-slate-500">
                              {plan.durationInDays} days duration
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {plan.description && (
                        <p className="text-slate-400 text-sm text-center">{plan.description}</p>
                      )}

                      {/* Features */}
                      {plan.features.length > 0 && (
                        <div className="space-y-2 xs:space-y-3">
                          <h4 className="font-semibold text-green-400 text-sm xs:text-base">✓ Benefits:</h4>
                          <ul className="space-y-1.5 xs:space-y-2">
                            {plan.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2 text-xs xs:text-sm text-slate-300">
                                <Icon.Play className="w-3.5 h-3.5 xs:w-4 xs:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Limitations */}
                      {plan.limitations.length > 0 && (
                        <div className="space-y-2 xs:space-y-3">
                          <h4 className="font-semibold text-slate-400 text-sm xs:text-base">Limitations:</h4>
                          <ul className="space-y-1.5 xs:space-y-2">
                            {plan.limitations.map((limitation, index) => (
                              <li key={index} className="flex items-start gap-2 text-xs xs:text-sm text-slate-400">
                                <span className="w-3.5 h-3.5 xs:w-4 xs:h-4 mt-0.5 flex-shrink-0">•</span>
                                {limitation}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Action Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurchase(plan.id);
                        }}
                        className={`w-full py-2.5 xs:py-3 px-4 xs:px-6 rounded-lg xs:rounded-xl font-semibold transition text-sm xs:text-base ${
                          plan.price.monthly === 0 && plan.price.yearly === 0
                            ? 'bg-slate-700 text-slate-300 cursor-default'
                            : selectedPlan === plan.id
                            ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                        disabled={(plan.price.monthly === 0 && plan.price.yearly === 0) || !plan.isActive}
                      >
                        {plan.price.monthly === 0 && plan.price.yearly === 0 
                          ? 'Free Plan' 
                          : !plan.isActive
                          ? 'Currently Unavailable'
                          : `Get ${plan.name}`
                        }
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-slate-400 text-lg">No subscription plans available at the moment.</div>
                <p className="text-slate-500 mt-2">Please check back later.</p>
              </div>
            )}

            {/* FAQ Section */}
            <div className="bg-white/5 backdrop-blur-lg rounded-xl xs:rounded-2xl border border-white/10 p-4 xs:p-6 sm:p-8">
              <h2 className="text-xl xs:text-2xl font-bold mb-4 xs:mb-6 text-center text-white">
                Frequently Asked Questions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2 text-white">Can I cancel anytime?</h3>
                    <p className="text-slate-400 text-sm">
                      Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-white">Do you offer refunds?</h3>
                    <p className="text-slate-400 text-sm">
                      We offer a 30-day money-back guarantee for all paid plans. No questions asked.
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2 text-white">Can I change plans?</h3>
                    <p className="text-slate-400 text-sm">
                      Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-white">Is there a free trial?</h3>
                    <p className="text-slate-400 text-sm">
                      Yes! We offer a 7-day free trial for all premium plans. No credit card required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && selectedPlanForPayment && (
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            planDetails={selectedPlanForPayment}
            onPaymentComplete={handlePaymentComplete}
          />
        )}
  

      <Footer/>
    </>
  );
}